import { useEffect, useState } from 'react'
import {
  Box, Button, Card, CardBody, CardHeader, Flex, FormControl, FormLabel, Grid, GridItem,
  HStack, Heading, Input, VStack, Menu, MenuButton, MenuList, MenuItem, Alert, AlertIcon, Spinner
} from '@chakra-ui/react'
import { UiBreadcrumb } from 'components/common'
import {
  createEdgeQuote, previewEdgeQuote, getEdgeQuoteById, getCustomerById, fetchDataSafely,
  getCommunicationOptionById, getPedestalDecorationOptionById, getVerticalSupportOptionById,
  getFooterStyleOptionById, getInstallationOptionById, getSiteSurveyOptionById,
  getShippingOptionById, getExistingSignOptionById, getDisplayPricingNewByQuoteId
} from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useImmer } from 'use-immer'
import { format } from 'date-fns'
import { EdgeCustomerForm } from './EdgeCustomerForm'
import { EdgeEmcForm } from './EdgeEmcForm'
import { EdgeCabinetForm } from './EdgeCabinetForm'
import { EdgePedestalForm } from './EdgePedestalForm'
// import { EdgeMonumentForm } from './EdgeMonumentForm'
import { EdgeSupportForm } from './EdgeSupportForm'
import { EdgeExistingSignForm } from './EdgeExistingSignForm'
import { EdgeInstallationShippingForm } from './EdgeInstallationShippingForm'
import { EdgeMowCurbForm, doesInstallationOptionSupportMowCurb } from './EdgeMowCurbForm'
import { EdgeSoftwareForm, SOFTWARE_OPTIONS } from './EdgeSoftwareForm'
import { EdgeAdditionalItemsForm } from './EdgeAdditionalItemsForm'
import { getAssetUrl, getProfessionalInstallationPermitFee, QUOTE_PAYMENT_TERMS_OPTIONS, STANDARD_PAINT_COLORS, defaultStructureFields, applySyncedStructureFields, parseStructureComponents, isTruthyFlag, STACK_MODES } from 'lib'
import { EdgePaymentTermsForm } from './EdgePaymentTermsForm'
import { EdgeCrateResultsForm } from './EdgeCrateResultsForm'
import { EdgeOverallStructureForm } from './EdgeOverallStructureForm'

const BreadcrumbNav = [
  {
    label: 'Standard Quotes',
    path: '/quotes/edge-quotes'
  },
  {
    label: 'Add',
    isCurrentPage: true
  }
]

const BreadcrumbNavDuplicate = [
  {
    label: 'Standard Quotes',
    path: '/quotes/edge-quotes'
  },
  {
    label: 'Duplicate',
    isCurrentPage: true
  }
]

const BRACKET_INSTALLATION_TYPES = ['wall_mount', 'retrofit']

export const AddEdgeQuote = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const duplicateFromId = searchParams.get('duplicateFrom')
  const { createToast, currentUser } = useAppContext()
  const [isDuplicating, setIsDuplicating] = useState(!!duplicateFromId)
  const [duplicateSourceNo, setDuplicateSourceNo] = useState(null)
  const [form, setForm] = useImmer({
    quoteDate: format(new Date(), 'yyyy-MM-dd'),
    quoteValidTo: format(new Date(new Date().getTime() + (90 * 24 * 60 * 60 * 1000)), 'yyyy-MM-dd'),
    customer: null,
    salesUserId: currentUser?.id,
    noEmc: false,
    emcModel: '',
    // emcPricingVersion 2 means that new display pricing system by formula. The original was fetching from a predefined table
    emcPricingVersion: 2,
    emcPricing: null,
    emcPricingId: 0,
    emcFace: '',
    emcBackWrapChecked: false,
    emcBackWrapCost: 0,
    emcSideVentChecked: false,
    emcSideVentCost: 0,
    emcCommunicationOption: null,
    emcAlternateOptions: [],
    emcPitch: 0,
    emcModsH: 0,
    emcModsL: 0,
    emcCost: 0,
    noCabinet: false,
    cabinetHeightIn: 0,
    cabinetLengthIn: 0,
    cabinetFace: '',
    cabinetStyle: '',
    cabinetFaceType: 'Polycarbonate',
    cphra: false,
    plpr: false,
    cabinetCost: 0,
    noPedestal: false,
    pedestalStyle: '',
    pedestalHeightIn: 0,
    pedestalLengthIn: 0,
    pedestalDecorationOption: null,
    pedestalCost: 0,
    pedestalAdditionalHeight: 0,
    noMonument: false,
    monumentStyle: '',
    monumentDescription: '',
    monumentCost: 0,
    numberOfSupports: 1,
    installationType: '',
    autoTubeSize: '',
    autoFooterDepthIn: 0,
    steelTubeLengthIn: 0,
    isSupportOverride: false,
    overrideTubeSize: '',
    plateCost: 0,
    verticalSupportOption: null,
    verticalSupportCost: 0,
    bracketOption: null,
    footerStyleOption: 0,
    footerStyleCost: 0,
    existingSignOption: null,
    existingSignCost: 0,
    installationOption: null,
    installationCost: 0,
    mowCurbEnabled: false,
    mowCurbCalculationType: '',
    mowCurbWidthFt: 0,
    mowCurbCost: 0,
    siteSurveyOption: 0,
    siteSurveyCost: 0,
    shippingOption: 0,
    shippingCost: 0,
    utsCarrierName: '',
    utsCarrierScac: '',
    utsServiceLevel: '',
    utsEstimateNumber: '',
    utsTransitDays: null,
    utsRateAmount: null,
    software: SOFTWARE_OPTIONS.Cloud,
    additionalItems: [],
    additionalItemsCost: 0,
    cabinetPaintColor: 'TBD',
    customColorChecked: false,
    customColorCost: 0,
    overage: 0,
    discount: 0,
    totalCost: 0,
    paymentTerms: '',
    ...defaultStructureFields()
  })
  const [isCreating, setIsCreating] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!form.installationType) {
      createToast('error', 'Error', 'A support installation type is required.')
      return
    }

    const isPoleBased = ['direct_burial', 'single_anchor', 'dual_anchor'].includes(form.installationType)
    const isBracketBased = ['wall_mount', 'retrofit'].includes(form.installationType)

    if (isBracketBased && !form.bracketOption) {
      createToast('error', 'Error', 'Please select a bracket or hardware option.')
      return
    }

    const bracketId = form.bracketOption?.id
    const verticalSupportOptionId = isPoleBased
      ? (form.verticalSupportOption?.id || 0)
      : (typeof bracketId === 'number' ? bracketId : 0)

    const data = {
      ...form,
      customerId: form.customer.id,
      emcAlternateOptions: JSON.stringify(form.emcAlternateOptions),
      emcCommunicationOptionId: form.emcCommunicationOption?.id || 0,
      emcCost: form.emcCost - form.emcBackWrapCost - form.emcSideVentCost,
      cabinetHeightFt: parseInt(form.cabinetHeightIn / 12),
      cabinetHeightIn: form.cabinetHeightIn % 12,
      cabinetLengthFt: parseInt(form.cabinetLengthIn / 12),
      cabinetLengthIn: form.cabinetLengthIn % 12,
      pedestalDecorationOptionId: form.pedestalDecorationOption?.id || 0,
      verticalSupportOptionId,
      footerStyleOptionId: form.footerStyleOption?.id || 0,
      footerStyleCost: 0,
      existingSignOptionId: form.existingSignOption.id,
      installationOptionId: form.installationOption.id,
      mowCurbEnabled: form.mowCurbEnabled,
      mowCurbCalculationType: form.mowCurbCalculationType,
      mowCurbWidthFt: form.mowCurbWidthFt,
      mowCurbCost: form.mowCurbCost,
      siteSurveyOptionId: form.siteSurveyOption.id,
      shippingOptionId: form.shippingOption.id,
      utsCarrierName: form.utsCarrierName || '',
      utsCarrierScac: form.utsCarrierScac || '',
      utsServiceLevel: form.utsServiceLevel || '',
      utsEstimateNumber: form.utsEstimateNumber || '',
      utsTransitDays: form.utsTransitDays,
      utsRateAmount: form.utsRateAmount,
      additionalItems: JSON.stringify(form.additionalItems),
      structureComponents: JSON.stringify(form.structureComponents || []),
      groundClearanceManual: !!form.groundClearanceManual,
      overallHeightManual: !!form.overallHeightManual,
      squareFootageManual: !!form.squareFootageManual
    }

    const buttonName = e.nativeEvent.submitter.name
    switch (buttonName) {
      case 'btn-create-quote':
        setIsCreating(true)
        try {
          const newQuote = await createEdgeQuote(data)
          createToast('success', 'Standard quote created')
          navigate('/quotes/edge-quotes', { state: { newQuote } })
          setIsCreating(false)
        } catch (err) {
          createToast('error', 'Error', err.response.data)
          setIsCreating(false)
        }
        break
      case 'btn-preview-quote':
        setIsPreviewing(true)
        try {
          const response = await previewEdgeQuote(data)
          window.open(getAssetUrl(response.pdfPath))
          setIsPreviewing(false)
        } catch (err) {
          createToast('error', 'Error', err.response.data)
          setIsPreviewing(false)
        }
        break
    }
  }

  // Prefill the form from an existing quote when duplicating
  useEffect(() => {
    if (!duplicateFromId) return

    const load = async () => {
      setIsDuplicating(true)
      try {
        const quote = await getEdgeQuoteById(duplicateFromId)
        if (!quote) {
          createToast('error', 'Error', 'Could not load the quote to duplicate.')
          setIsDuplicating(false)
          return
        }
        setDuplicateSourceNo(quote.quoteNo)

        const fetchOptionIfSet = (id, fetchFn) => {
          const numericId = Number(id)
          if (!Number.isFinite(numericId) || numericId <= 0) return Promise.resolve(null)
          return fetchDataSafely(() => fetchFn(numericId))
        }

        const [
          customer, emcCommunicationOption, pedestalDecorationOption,
          verticalSupportOption, footerStyleOption, installationOption,
          siteSurveyOption, shippingOption, existingSignOption
        ] = await Promise.all([
          fetchOptionIfSet(quote.customerId, getCustomerById),
          fetchOptionIfSet(quote.emcCommunicationOptionId, getCommunicationOptionById),
          fetchOptionIfSet(quote.pedestalDecorationOptionId, getPedestalDecorationOptionById),
          fetchOptionIfSet(quote.verticalSupportOptionId, getVerticalSupportOptionById),
          fetchOptionIfSet(quote.footerStyleOptionId, getFooterStyleOptionById),
          fetchOptionIfSet(quote.installationOptionId, getInstallationOptionById),
          fetchOptionIfSet(quote.siteSurveyOptionId, getSiteSurveyOptionById),
          fetchOptionIfSet(quote.shippingOptionId, getShippingOptionById),
          fetchOptionIfSet(quote.existingSignOptionId, getExistingSignOptionById)
        ])

        // EMC pitch / module dimensions live in display_pricings_new, keyed by quote id
        let emcPitch = 0
        let emcModsH = 0
        let emcModsL = 0
        if (quote.noEmc !== 1 && quote.emcPricingVersion === 2) {
          const displayPricing = await fetchDataSafely(() => getDisplayPricingNewByQuoteId(quote.id))
          if (displayPricing) {
            emcPitch = displayPricing.pitch || 0
            emcModsH = displayPricing.modsH || 0
            emcModsL = displayPricing.modsL || 0
          }
        }

        const isBracketBased = BRACKET_INSTALLATION_TYPES.includes(quote.installationType)

        // Give duplicated additional items fresh sequential ids
        const additionalItems = (quote.additionalItems ? JSON.parse(quote.additionalItems) : [])
          .map((item, index) => ({ ...item, id: index + 1 }))

        const today = format(new Date(), 'yyyy-MM-dd')
        const validTo = format(new Date(new Date().getTime() + (90 * 24 * 60 * 60 * 1000)), 'yyyy-MM-dd')

        setForm(() => ({
          quoteDate: today,
          quoteValidTo: validTo,
          customer: customer || null,
          salesUserId: quote.salesUserId || currentUser?.id,
          noEmc: quote.noEmc === 1,
          emcModel: quote.emcModel || '',
          emcPricingVersion: quote.emcPricingVersion ?? 2,
          emcPricing: null,
          emcPricingId: quote.emcPricingId || 0,
          emcFace: quote.emcFace || '',
          emcBackWrapChecked: quote.emcBackWrapChecked === 1,
          emcBackWrapCost: quote.emcBackWrapCost || 0,
          emcSideVentChecked: quote.emcSideVentChecked === 1,
          emcSideVentCost: quote.emcSideVentCost || 0,
          emcCommunicationOption: emcCommunicationOption || null,
          emcAlternateOptions: quote.emcAlternateOptions ? JSON.parse(quote.emcAlternateOptions) : [],
          emcPitch,
          emcModsH,
          emcModsL,
          emcCost: quote.emcCost || 0,
          noCabinet: quote.noCabinet === 1,
          cabinetHeightIn: (quote.cabinetHeightFt || 0) * 12 + (quote.cabinetHeightIn || 0),
          cabinetLengthIn: (quote.cabinetLengthFt || 0) * 12 + (quote.cabinetLengthIn || 0),
          cabinetFace: quote.cabinetFace || '',
          cabinetStyle: quote.cabinetStyle || '',
          cabinetFaceType: quote.cabinetFaceType || 'Polycarbonate',
          cphra: quote.cphra === 1,
          plpr: quote.plpr === 1,
          cabinetCost: quote.cabinetCost || 0,
          noPedestal: quote.noPedestal === 1,
          pedestalStyle: quote.pedestalStyle || '',
          pedestalHeightIn: quote.pedestalHeightIn || 0,
          pedestalLengthIn: quote.pedestalLengthIn || 0,
          pedestalDecorationOption: pedestalDecorationOption || null,
          pedestalCost: quote.pedestalCost || 0,
          pedestalAdditionalHeight: quote.pedestalAdditionalHeight || 0,
          noMonument: quote.noMonument === 1,
          monumentStyle: quote.monumentStyle || '',
          monumentDescription: quote.monumentDescription || '',
          monumentCost: quote.monumentCost || 0,
          numberOfSupports: quote.numberOfSupports || 1,
          installationType: quote.installationType || '',
          autoTubeSize: quote.autoTubeSize || '',
          autoFooterDepthIn: quote.autoFooterDepthIn || 0,
          steelTubeLengthIn: quote.steelTubeLengthIn || 0,
          isSupportOverride: quote.isSupportOverride === 1,
          overrideTubeSize: quote.overrideTubeSize || '',
          plateCost: quote.plateCost || 0,
          verticalSupportOption: isBracketBased ? null : (verticalSupportOption || null),
          verticalSupportCost: quote.verticalSupportCost || 0,
          bracketOption: isBracketBased ? (verticalSupportOption || null) : null,
          footerStyleOption: footerStyleOption || null,
          footerStyleCost: quote.footerStyleCost || 0,
          existingSignOption: existingSignOption || null,
          existingSignCost: quote.existingSignCost || 0,
          installationOption: installationOption || null,
          installationCost: quote.installationCost || 0,
          mowCurbEnabled: quote.mowCurbEnabled === 1,
          mowCurbCalculationType: quote.mowCurbCalculationType || '',
          mowCurbWidthFt: quote.mowCurbWidthFt || 0,
          mowCurbCost: quote.mowCurbCost || 0,
          siteSurveyOption: siteSurveyOption || null,
          siteSurveyCost: quote.siteSurveyCost || 0,
          shippingOption: shippingOption || null,
          shippingCost: quote.shippingCost || 0,
          utsCarrierName: '',
          utsCarrierScac: '',
          utsServiceLevel: '',
          utsEstimateNumber: '',
          utsTransitDays: null,
          utsRateAmount: null,
          software: quote.software || SOFTWARE_OPTIONS.Cloud,
          additionalItems,
          additionalItemsCost: quote.additionalItemsCost || 0,
          customColorChecked: quote.customColorChecked === 1,
          customColorCost: quote.customColorCost || 0,
          overage: quote.overage || 0,
          discount: quote.discount || 0,
          totalCost: quote.totalCost || 0,
          paymentTerms: quote.paymentTerms || '',
          emcStackMode: quote.emcStackMode || STACK_MODES.Stacked,
          cabinetStackMode: quote.cabinetStackMode || STACK_MODES.Stacked,
          structureComponents: parseStructureComponents(quote.structureComponents),
          groundClearanceFt: quote.groundClearanceFt || 0,
          groundClearanceManual: isTruthyFlag(quote.groundClearanceManual),
          overallHeightFt: quote.overallHeightFt || 0,
          overallHeightManual: isTruthyFlag(quote.overallHeightManual),
          squareFootage: quote.squareFootage || 0,
          squareFootageManual: isTruthyFlag(quote.squareFootageManual)
        }))
      } catch (err) {
        createToast('error', 'Error', err?.response?.data || 'Failed to duplicate the quote.')
      } finally {
        setIsDuplicating(false)
      }
    }
    load()
  }, [duplicateFromId])

  // Update installation cost to include mow curb cost when installation option or mow curb changes
  useEffect(() => {
    const baseInstallationCost = form.installationOption?.cost || 0
    const mowCurbCost = form.mowCurbEnabled ? form.mowCurbCost : 0
    setForm(draft => {
      draft.installationCost = parseFloat((baseInstallationCost + mowCurbCost).toFixed(2))
    })
  }, [form.installationOption?.cost, form.mowCurbEnabled, form.mowCurbCost])

  useEffect(() => {
    setForm(draft => {
      draft.totalCost = parseFloat((form.emcCost + form.cabinetCost + form.pedestalCost + form.monumentCost + form.verticalSupportCost + form.plateCost + form.existingSignCost +
        parseFloat(form.installationCost) + parseFloat(form.siteSurveyCost) + parseFloat(form.shippingCost) + form.additionalItemsCost + form.customColorCost +
        getProfessionalInstallationPermitFee(form.installationOption) + parseFloat(form.overage) - parseFloat(form.discount)).toFixed(2))
    })
  }, [
    form.emcCost, form.cabinetCost, form.pedestalCost, form.monumentCost, form.verticalSupportCost, form.plateCost, form.existingSignCost, form.installationCost,
    form.siteSurveyCost, form.shippingCost, form.additionalItemsCost, form.customColorCost, form.overage, form.discount, form.installationOption
  ])

  useEffect(() => {
    if (form.installationOption?.id === 6) {
      setForm(draft => {
        draft.paymentTerms = QUOTE_PAYMENT_TERMS_OPTIONS.ClientInstall
      })
    } else {
      setForm(draft => {
        draft.paymentTerms = QUOTE_PAYMENT_TERMS_OPTIONS.Standard
      })
    }
  }, [form.installationOption])

  // Reset mow curb options when installation option changes to one that doesn't support mow curb
  useEffect(() => {
    if (!doesInstallationOptionSupportMowCurb(form.installationOption?.id)) {
      setForm(draft => {
        draft.mowCurbEnabled = false
        draft.mowCurbCalculationType = ''
        draft.mowCurbWidthFt = 0
        draft.mowCurbCost = 0
      })
    }
  }, [form.installationOption?.id])

  useEffect(() => {
    setForm(draft => {
      applySyncedStructureFields(draft)
    })
  }, [
    form.noPedestal, form.pedestalHeightIn, form.noEmc, form.emcModsH, form.emcModsL, form.emcStackMode,
    form.noCabinet, form.cabinetHeightIn, form.cabinetLengthIn, form.cabinetStackMode, form.structureComponents,
    form.groundClearanceFt, form.groundClearanceManual, form.overallHeightFt, form.overallHeightManual,
    form.squareFootage, form.squareFootageManual
  ])

  return (
    <>
      <UiBreadcrumb items={duplicateFromId ? BreadcrumbNavDuplicate : BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">{duplicateFromId ? 'Duplicate Standard Quote' : 'Create Standard Quote'}</Heading>
        </Flex>
        {duplicateFromId && duplicateSourceNo && (
          <Alert status="info" mb={4} borderRadius="md">
            <AlertIcon />
            Duplicated from Quote #{duplicateSourceNo}. Review the values, make any changes, then create the new quote.
          </Alert>
        )}
        {isDuplicating && (
          <Flex align="center" gap={3} mb={4}>
            <Spinner size="sm" />
            <Heading size="sm" color="gray.500">Loading quote details…</Heading>
          </Flex>
        )}
        <Card borderRadius={0}>
          <CardHeader bg="gray.500" p={4}>
            <Heading color="white" size="sm">Information</Heading>
          </CardHeader>
          <CardBody p={8} m="0 auto">
            <form onSubmit={handleSubmit}>
              <VStack gap={10}>
                <HStack gap={10} maxW="100%" width="600px" alignSelf="flex-start">
                  <FormControl isRequired>
                    <FormLabel>Date</FormLabel>
                    <Input type="date" name="quoteDate" value={form.quoteDate} onChange={handleChange} />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Valid To</FormLabel>
                    <Input type="date" name="quoteValidTo" value={form.quoteValidTo} onChange={handleChange} />
                  </FormControl>
                </HStack>
                <EdgeCustomerForm
                  form={form}
                  setForm={setForm}
                />
                <EdgeEmcForm
                  form={form}
                  setForm={setForm}
                />
                <EdgeCabinetForm
                  form={form}
                  setForm={setForm}
                />
                <EdgePedestalForm
                  form={form}
                  setForm={setForm}
                />
                <EdgeOverallStructureForm
                  form={form}
                  setForm={setForm}
                />
                {/* <EdgeMonumentForm
                  form={form}
                  setForm={setForm}
                /> */}
                <EdgeSupportForm
                  form={form}
                  setForm={setForm}
                />
                <Box>
                  <Heading size="md" mb={4}>Color Selection</Heading>
                  <Grid templateColumns="5fr 1fr" columnGap={10} w="900px" maxW="100%">
                    <GridItem>
                      <FormControl>
                        <FormLabel>Paint Color</FormLabel>
                        <Menu>
                          <MenuButton
                            as={Button}
                            rightIcon={<span />}
                            variant="outline"
                            minW="280px"
                            justifyContent="flex-start"
                            textAlign="left"
                            fontWeight="normal"
                            borderWidth="1px"
                            borderColor="gray.400"
                            _hover={{ borderColor: 'gray.500' }}
                            _expanded={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
                          >
                            <HStack spacing={3}>
                              <Box
                                w="20px"
                                h="20px"
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor="gray.300"
                                bg={STANDARD_PAINT_COLORS.find(c => c.value === (form.cabinetPaintColor || 'TBD'))?.hex ?? 'gray.200'}
                              />
                              <span>
                                {STANDARD_PAINT_COLORS.find(c => c.value === (form.cabinetPaintColor || 'TBD'))?.label ?? 'T.B.D.'}
                              </span>
                            </HStack>
                          </MenuButton>
                          <MenuList maxH="320px" overflowY="auto">
                            {STANDARD_PAINT_COLORS.map((color) => (
                              <MenuItem
                                key={color.value}
                                onClick={() => setForm(draft => {
                                  draft.cabinetPaintColor = color.value
                                  draft.customColorChecked = color.value === 'Custom'
                                  draft.customColorCost = color.value === 'Custom' ? 250 : 0
                                })}
                              >
                                <HStack spacing={3}>
                                  <Box
                                    w="20px"
                                    h="20px"
                                    flexShrink={0}
                                    borderRadius="md"
                                    borderWidth="1px"
                                    borderColor="gray.300"
                                    bg={color.hex ?? 'gray.200'}
                                  />
                                  <span>{color.label}</span>
                                </HStack>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl>
                        <FormLabel>Paint Color Cost</FormLabel>
                        <Input value={form.customColorCost} readOnly />
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>
                <EdgeExistingSignForm
                  form={form}
                  setForm={setForm}
                />
                <EdgeInstallationShippingForm
                  form={form}
                  setForm={setForm}
                />
                <EdgeMowCurbForm
                  form={form}
                  setForm={setForm}
                />
                <EdgeSoftwareForm
                  form={form}
                  setForm={setForm}
                />
                <EdgePaymentTermsForm
                  form={form}
                  setForm={setForm}
                />
                <EdgeAdditionalItemsForm
                  form={form}
                  setForm={setForm}
                />
                <Grid
                  templateColumns="5fr 1fr"
                  rowGap={5}
                  columnGap={10}
                  w="900px"
                  maxW="100%"
                >
                  <GridItem></GridItem>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Overage</FormLabel>
                      <Input
                        type="number"
                        value={form.overage}
                        onChange={e => {
                          setForm(draft => {
                            draft.overage = e.target.value
                          })
                        }}
                        onBlur={e => {
                          setForm(draft => {
                            draft.overage = parseFloat(e.target.value || 0).toFixed(2)
                          })
                        }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem></GridItem>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Discount</FormLabel>
                      <Input
                        type="number"
                        value={form.discount}
                        onChange={e => {
                          setForm(draft => {
                            draft.discount = e.target.value
                          })
                        }}
                        onBlur={e => {
                          setForm(draft => {
                            draft.discount = parseFloat(e.target.value || 0).toFixed(2)
                          })
                        }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem></GridItem>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel>Total</FormLabel>
                      <Input
                        type="number"
                        value={form.totalCost}
                        onChange={e => {
                          setForm(draft => {
                            draft.totalCost = e.target.value
                          })
                        }}
                        onBlur={e => {
                          setForm(draft => {
                            draft.totalCost = parseFloat(e.target.value || 0).toFixed(2)
                          })
                        }}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
                <EdgeCrateResultsForm form={form} setForm={setForm} />
                <HStack gap={2}>
                  <Button type="submit" name="btn-create-quote" colorScheme="blue" isLoading={isCreating}>Create Quote</Button>
                  <Button type="submit" name="btn-preview-quote" colorScheme="gray" isLoading={isPreviewing}>Preview Quote</Button>
                </HStack>
              </VStack>
            </form>
          </CardBody>
        </Card>
      </Box>
    </>
  )
}
